import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util'; // Corrected path for api-response.util
import { Locale, SaveIntakeOptions } from './workout.service'; // Assuming Locale is shared
import { toLocalIso } from '@/utils/datetime';

export interface AvailableMeasure {
  key: string;
  label: string;
  gramsEquivalent: number;
  isDefault: boolean;
  defaultQuantity: number;
}

// Enums for food properties
// FoodCategory is now dynamic, so we use string type
export type FoodCategory = string;

export enum MeasurementUnit {
  GRAM = 'gram',
  MILLILITER = 'milliliter',
  PIECE = 'piece',
  CUP = 'cup',
  TABLESPOON = 'tablespoon',
  TEASPOON = 'teaspoon',
  SLICE = 'slice',
  PORTION = 'portion',
  LITER = 'liter',
  OTHER = 'other',
}

export enum FoodSourceType {
  SYSTEM = 'system',
  USER = 'user',
  ALL = 'all',
}
export type FoodSortBy = 'id' | 'category' | 'name';
export type SortOrder = 'ASC' | 'DESC'; // Already defined in workout.service, but good to have here if needed independently

// Food DTOs
export interface FoodResponseDto {
  id: number;
  name: string;
  category: FoodCategory;
  createdByUserId: number | null;
  measurementUnit: string; // Localized string
  rawMeasurementUnit: MeasurementUnit; // Non-localized enum value
  
  // Macros per 100g
  caloriesPer100g: number | null;
  proteinPer100g: number | null;
  fatPer100g: number | null;
  carbsPer100g: number | null;
  fiberPer100g: number | null;

  defaultMeasurementValue: number | null;
  gramsPerUnit: number | null; // New field for grams per unit
  
  nameI18n: { [key in Locale]?: string };
  isStructured: boolean;
  isSystem: boolean; // Added isSystem
  usageCount: number; // Added usageCount
  availableTranslations: { [key in Locale]?: boolean }; // Added availableTranslations
  details?: string | null;
  imageUrl?: string | null; // Added imageUrl
  externalLink?: string | null; // Added externalLink
  legacyId?: string | null; // Added legacyId
  legacyEntity?: 'product' | 'dish' | null; // Added legacyEntity
  legacyImg?: string | null; // Added legacyImg
  availableMeasures?: AvailableMeasure[];
  isSitePublished?: boolean;
}

export interface FoodSuggestion {
  id: number | null;
  name: string; // Changed back to required
  category: string;
  measurementUnit?: string;
  rawMeasurementUnit?: MeasurementUnit;
  caloriesPerUnit?: number;
  caloriesPer100g?: number;
  proteinPer100g?: number | string | null;
  fatPer100g?: number | string | null;
  carbsPer100g?: number | string | null;
  fiberPer100g?: number | string | null;
  defaultMeasurementValue?: number | null;
  gramsPerUnit?: number; // New field for grams per unit
  isStructured: boolean; // Changed back to required
  isSystem: boolean; // Added isSystem
  usageCount?: number; // Added usageCount (optional as it might not be in suggestion)
  imageUrl?: string | null; // Added imageUrl
  externalLink?: string | null; // Added externalLink
  availableMeasures?: AvailableMeasure[];
}

export interface PaginatedFoodsResponseDto {
  data: FoodResponseDto[];
  meta: {
    total: number;
    page: number;
    limit: number;
  };
}

export interface FoodOptionsResponseDto {
  categories: { value: string; label: string }[];
  measurementUnits: { value: string; label: string }[];
  experts: { id: number; name: string }[];
  sourceTypes: { value: string; label: string }[];
}

// Query Parameters
export interface GetFoodsParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: FoodSortBy;
  sortOrder?: SortOrder;
  category?: FoodCategory;
  sourceType?: FoodSourceType;
  expertId?: number; // Changed from userId to expertId
  isStructured?: boolean; // Added isStructured filter
  isSitePublished?: boolean;
}

// Food Event Payload (already defined, keeping for completeness)
// V2 payload — backend calculates qtyG from quantity × gramsEquivalent
export interface FoodEventPayloadV2 {
  foodId?: number;
  customName?: string;
  measureKey: string;
  quantity: number;
  scheduledLdt: string; // local datetime string (no Z suffix)
  isCompleted: boolean;
  calendarId?: number;
  // Нутрієнти на 100г для кастомного продукту
  caloriesPer100g?: number;
  proteinPer100g?: number;
  fatPer100g?: number;
  carbsPer100g?: number;
  fiberPer100g?: number;
}

// DTO for creating a food
export interface CreateFoodDto {
  nameI18n: { [key in Locale]?: string };
  category: FoodCategory;
  measurementUnit: MeasurementUnit;
  
  caloriesPer100g?: number | null;
  proteinPer100g?: number | null;
  fatPer100g?: number | null;
  carbsPer100g?: number | null;
  fiberPer100g?: number | null;
  
  gramsPerUnit?: number | null;
  defaultMeasurementValue?: number | null;
  imageId?: number | null; // Added imageId
  externalLink?: string | null; // Added externalLink
}

// DTO for updating a food
export interface UpdateFoodDto {
  nameI18n?: { [key in Locale]?: string };
  category?: FoodCategory;
  measurementUnit?: MeasurementUnit;
  
  caloriesPer100g?: number | null;
  proteinPer100g?: number | null;
  fatPer100g?: number | null;
  carbsPer100g?: number | null;
  fiberPer100g?: number | null;
  
  gramsPerUnit?: number | null;
  defaultMeasurementValue?: number | null;
  imageId?: number | null; // Added imageId
  externalLink?: string | null; // Added externalLink
}

// DTO for merging foods
export interface MergeFoodDto {
  sourceFoodId: number;
  targetFoodId: number;
}

class FoodService {
  private readonly BASE_URL = '/lib/foods';
  private readonly INTAKE_URL = '/calendars/events/food';
  private readonly CALENDAR_EVENT_URL = '/calendars/events'; // Base URL for calendar events

  async getFoods(params: GetFoodsParams): Promise<PaginatedFoodsResponseDto> {
    const response = await api.get<ApiResponse<FoodResponseDto[]>>(`${this.BASE_URL}`, {
      params,
    });
    const unwrapped = unwrapApiResponse<FoodResponseDto[]>(response);
    return {
      data: unwrapped,
      meta: response.data.meta, // Assuming meta is directly on response.data
    };
  }

  async getFoodSuggestions(query: string): Promise<FoodSuggestion[]> {
    const response = await api.get<ApiResponse<FoodSuggestion[]>>(`${this.BASE_URL}/search`, { params: { query } });
    return unwrapApiResponse<FoodSuggestion[]>(response);
  }

  async searchSystemFoods(query: string, limit: number = 10): Promise<FoodResponseDto[]> {
    const params: GetFoodsParams = {
      search: query,
      limit: limit,
      sourceType: FoodSourceType.SYSTEM,
    };
    const response = await this.getFoods(params);
    return response.data;
  }

  async getFoodForEdit(id: number): Promise<FoodResponseDto> {
    const response = await api.get<ApiResponse<FoodResponseDto>>(`${this.BASE_URL}/${id}`);
    return unwrapApiResponse<FoodResponseDto>(response);
  }

  async createFood(payload: CreateFoodDto): Promise<FoodResponseDto> {
    const response = await api.post<ApiResponse<FoodResponseDto>>(`${this.BASE_URL}`, payload);
    return unwrapApiResponse<FoodResponseDto>(response);
  }

  async updateFood(id: number, payload: UpdateFoodDto): Promise<FoodResponseDto> {
    const response = await api.patch<ApiResponse<FoodResponseDto>>(`${this.BASE_URL}/${id}`, payload);
    return unwrapApiResponse<FoodResponseDto>(response);
  }

  async deleteFood(id: number): Promise<void> {
    await api.delete<ApiResponse<void>>(`${this.BASE_URL}/${id}`);
  }

  async mergeFoods(payload: MergeFoodDto): Promise<{ removedFood: FoodResponseDto; targetFood: FoodResponseDto; updatedCalendarEvents: number }> {
    const response = await api.post<ApiResponse<{ removedFood: FoodResponseDto; targetFood: FoodResponseDto; updatedCalendarEvents: number }>>(`${this.BASE_URL}/merge`, payload);
    return unwrapApiResponse<{ removedFood: FoodResponseDto; targetFood: FoodResponseDto; updatedCalendarEvents: number }>(response);
  }

  async createFoodEventV2(payload: FoodEventPayloadV2, clientId?: string | number): Promise<any> {
    const url = clientId ? `/client/${clientId}/calendar/events/food/v2` : `${this.INTAKE_URL}/v2`;
    const response = await api.post<ApiResponse<any>>(url, payload);
    return unwrapApiResponse<any>(response);
  }

  async updateFoodEventV2(eventId: number, payload: FoodEventPayloadV2): Promise<any> {
    const response = await api.put<ApiResponse<any>>(`${this.CALENDAR_EVENT_URL}/${eventId}/food/v2`, payload);
    return unwrapApiResponse<any>(response);
  }

  async getFoodOptions(): Promise<FoodOptionsResponseDto> {
    const response = await api.get<ApiResponse<FoodOptionsResponseDto>>(`${this.BASE_URL}/options`);
    return unwrapApiResponse<FoodOptionsResponseDto>(response);
  }
}

export const foodService = new FoodService();

// Export individual functions for convenience, similar to workout.service
export const getFoodSuggestions = (query: string) => foodService.getFoodSuggestions(query);
export const getFoodForEdit = (id: number) => foodService.getFoodForEdit(id);
export const createFoodEventV2 = (payload: FoodEventPayloadV2, clientId?: string | number) => foodService.createFoodEventV2(payload, clientId);
export const updateFoodEventV2 = (eventId: number, payload: FoodEventPayloadV2) => foodService.updateFoodEventV2(eventId, payload);
export const getFoods = (params: GetFoodsParams) => foodService.getFoods(params);
export const searchSystemFoods = (query: string, limit?: number) => foodService.searchSystemFoods(query, limit);
export const createFood = (payload: CreateFoodDto) => foodService.createFood(payload);
export const updateFood = (id: number, payload: UpdateFoodDto) => foodService.updateFood(id, payload);
export const deleteFood = (id: number) => foodService.deleteFood(id);
export const mergeFoods = (payload: MergeFoodDto) => foodService.mergeFoods(payload);
export const getFoodOptions = () => foodService.getFoodOptions();

/** Зберегти їжу з AddFoodEventModal (measureKey + quantity; кастомний продукт - у грамах з нутрієнтами на 100 г). */
export const saveFoodEvent = (data: any, { eventId, clientId }: SaveIntakeOptions = {}) => {
  const payload: FoodEventPayloadV2 = {
    scheduledLdt: toLocalIso(data.scheduledAt),
    isCompleted: data.isCompleted,
    calendarId: data.calendarId,
    ...(data.foodId ? { foodId: data.foodId } : { customName: data.name }),
    measureKey: data.measureKey,
    quantity: data.quantity,
    ...(data.caloriesPer100g !== undefined && { caloriesPer100g: data.caloriesPer100g }),
    ...(data.proteinPer100g !== undefined && { proteinPer100g: data.proteinPer100g }),
    ...(data.fatPer100g !== undefined && { fatPer100g: data.fatPer100g }),
    ...(data.carbsPer100g !== undefined && { carbsPer100g: data.carbsPer100g }),
    ...(data.fiberPer100g !== undefined && { fiberPer100g: data.fiberPer100g }),
  };
  return eventId ? foodService.updateFoodEventV2(eventId, payload) : foodService.createFoodEventV2(payload, clientId);
};
